import { Icon, Tag } from '@ps/isp-web-component-library';
import type { MyBetPart, ResultType } from '@ps/isp-web-services-types';
import { PriceType } from '@ps/isp-web-services-types';
import { type Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import classNames from 'clsx';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import PriceBoost from '@swf/generated/sports-icons/priceBoost';
import StarSub from '@swf/generated/sports-icons/starSub';
import EachWayPlaceTerms from '@swf/sports/components/EachWayPlaceTerms/EachWayPlaceTerms';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';

import { isActiveEventBasedMyBetPart } from '../../../../../../../../utils/isActivePart';
import BetDeduction from '../../../BetDeduction/BetDeduction';
import BetEventStatus from '../../../BetEventStatus/BetEventStatus';
import BetTracking from '../../../BetTracking/BetTracking';
import LiveOptionsPanel from '../../../LiveOptionsPanel/LiveOptionsPanel';
import EventLink from '../EventLink/EventLink';
import EventTypeIcon from '../EventTypeIcon/EventTypeIcon';
import MarketStatus, { marketStatusResultMap } from '../MarketStatus/MarketStatus';
import OddsContent from '../OddsContent/OddsContent';
import SelectionName from '../SelectionName/SelectionName';

import { TXTWCL_Star_Sub_enabled_selection, TXTWCL_odds_boost } from './Part.messages.json';
import styles from './Part.scss';

export interface Props {
  part: MyBetPart;
  isSettled?: boolean;
  result?: ResultType;
  currency: Currency;
  cashedOutAmount?: number;
  betId?: string;
}

const Part: React.FC<Props> = ({ betId, part, isSettled, result, currency, cashedOutAmount }) => {
  const { formatMessage } = useRegulatoryIntl();
  const isEnhancedBetTrackingEnabled = useFeature(FeatureFlag.ISPEnableEnhancedBetTracking);
  const eventTypeId = Number(part.sportId);
  const hasEachWay = part.eachwayFactor && part.eachwayPlaces;
  const displayStartingPrice = !isSettled && part.priceType === PriceType.STARTING;
  const displayBetTracking =
    isEnhancedBetTrackingEnabled && !isSettled && !cashedOutAmount && !result;
  const hasPrefix = Boolean(part.odds || hasEachWay || displayStartingPrice);
  const deadHeat = Boolean(part.deadHeatEachwayDeductions || part.deadHeatWinDeductions);

  const isValidMarketStatus = result ? result in marketStatusResultMap : false;
  const shouldDisplayOddsContent = isSettled || isValidMarketStatus;

  return (
    <div className={styles.wrapper} key={part.eventMarketTemplateDescription}>
      <div className={styles.details}>
        <EventTypeIcon eventTypeId={eventTypeId} isDeadHeat={deadHeat} isInPlay={part.inPlay} />
        <div className={styles.eventDetailsContainer}>
          <div className={classNames(styles.eventDetails, result === 'VOID' && styles.voided)}>
            <span className={styles.selection}>
              <div className={styles.selectionTitle}>
                {part.eventMarketTemplateDescription}{' '}
                {shouldDisplayOddsContent ? (
                  <OddsContent
                    part={part}
                    isVisibleStartingPrice={displayStartingPrice}
                    hasPrefix={hasPrefix}
                  />
                ) : null}
              </div>
              {isActiveEventBasedMyBetPart(part) && part.isStarSubEnabled && (
                <Icon
                  icon={StarSub}
                  className={styles.starSubIcon}
                  label={formatMessage(TXTWCL_Star_Sub_enabled_selection)}
                  size="xs"
                />
              )}
              {isOddsBoostMarket(part.marketType) && (
                <Icon
                  className={styles.oddsboostIcon}
                  icon={PriceBoost}
                  label={formatMessage(TXTWCL_odds_boost)}
                  size="xs"
                />
              )}

              {!shouldDisplayOddsContent ? (
                <Tag className={styles.oddsTag} variant="tracker">
                  <OddsContent part={part} isVisibleStartingPrice={displayStartingPrice} />
                </Tag>
              ) : null}

              <MarketStatus result={result} />
            </span>

            <div className={styles.selectionName}>
              <SelectionName part={part} />
            </div>

            <div className={styles.selectionLink}>
              <EventLink part={part} isSettled={isSettled} />
            </div>

            {hasEachWay && part.eachwayPlaces && (
              <EachWayPlaceTerms
                placeTerms={{
                  deductionFraction: `${part.eachwayFactor?.numerator}/${part.eachwayFactor?.denominator}`,
                  numPlaces: part.eachwayPlaces
                }}
              />
            )}

            <BetEventStatus part={part} isSettled={isSettled} />
          </div>

          {!!part.rule4Deductions && (
            <BetDeduction
              type="rule4"
              value={<CurrencyAmount value={part.rule4Deductions} currency={currency} />}
            />
          )}
          {!!part.deadHeatEachwayDeductions && <BetDeduction type="deadHeatEachway" />}
          {!!part.deadHeatWinDeductions && <BetDeduction type="deadHeatWin" />}
          {displayBetTracking && (
            <BetTracking
              outcomeDefinitionExp={part.outcomeDefinitionExp}
              eventTypeId={eventTypeId}
              eventId={part.eventId}
              selectionId={part.selectionId}
            />
          )}
        </div>
      </div>

      {!isSettled ? (
        <LiveOptionsPanel part={part} betId={betId} accordionIds={betId ? [betId] : undefined} />
      ) : null}
    </div>
  );
};

export default Part;
